import { supabase } from './supabase';

export interface SignUpData {
  email: string;
  password: string;
  name: string;
  organization: string;
  role: string;
  isAdmin?: boolean;
  stakeholderType?: string;
  stakeholderDetails?: Record<string, any>;
  plan: string;
}

export interface AuthError extends Error {
  status?: number;
  code?: string;
}

// Helper function for delay between retries
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Retry function with exponential backoff
async function retryOperation<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      lastError = error;
      if (attempt < maxRetries - 1) {
        const waitTime = baseDelay * Math.pow(2, attempt);
        await delay(waitTime);
      }
    }
  }
  
  throw lastError;
}

export const signUp = async (data: SignUpData) => {
  try {
    // Input validation
    if (!data.email?.includes('@') || !data.password || data.password.length < 8) {
      throw new Error('Invalid email or password');
    }

    // Create user account
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: {
          full_name: data.name,
          organization: data.organization,
          role: data.role
        }
      }
    });

    if (authError) throw authError;
    if (!authData.user) throw new Error('User creation failed');

    // Wait a moment for the user profile to be created by the database trigger
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Assign role if specified
    if (data.isAdmin) {
      try {
        const { error: roleError } = await supabase.rpc('assign_role', {
          p_user_id: authData.user.id,
          p_role_name: 'admin'
        });
        
        if (roleError) {
          console.error('Role assignment error:', roleError);
        }
      } catch (roleErr) {
        console.error('Failed to assign admin role:', roleErr);
      }
    } else {
      // Assign client role by default
      try {
        const { error: roleError } = await supabase.rpc('assign_role', {
          p_user_id: authData.user.id,
          p_role_name: 'client'
        });
        
        if (roleError) {
          console.error('Role assignment error:', roleError);
        }
      } catch (roleErr) {
        console.error('Failed to assign client role:', roleErr);
      }
    }

    // Update stakeholder details if provided
    if (data.stakeholderType || data.stakeholderDetails) {
      const updateData: Record<string, any> = {};
      
      if (data.stakeholderType) {
        updateData.stakeholder_type = data.stakeholderType;
      }
      
      if (data.stakeholderDetails && Object.keys(data.stakeholderDetails).length > 0) {
        updateData.stakeholder_details = data.stakeholderDetails;
      }

      if (Object.keys(updateData).length > 0) {
        const { error: profileError } = await supabase
          .from('user_profiles')
          .update(updateData)
          .eq('id', authData.user.id);

        if (profileError) {
          console.error('Profile update error:', profileError);
          // Continue with subscription creation even if profile update fails
        }
      }
    }

    // Create subscription
    const { error: subscriptionError } = await supabase
      .from('subscriptions')
      .insert({
        user_id: authData.user.id,
        plan: ['free', 'basic', 'premium'].includes(data.plan) ? data.plan : 'free',
        status: 'active',
        expires_at: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
      });

    if (subscriptionError) throw subscriptionError;

    return { 
      success: true, 
      user: authData.user,
      message: 'Account created successfully'
    };

  } catch (error: any) {
    const authError: AuthError = new Error(
      error.message || 'An error occurred during signup'
    );
    authError.status = error.status;
    authError.code = error.code;
    
    console.error('Signup error:', {
      message: authError.message,
      status: authError.status,
      code: authError.code,
      timestamp: new Date().toISOString()
    });

    return { 
      success: false, 
      error: authError.message,
      code: authError.code
    };
  }
};

export const signIn = async (email: string, password: string) => {
  try {
    // Use the provided email and password parameters
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email,
      password: password
    });

    if (error) throw error;

    return {
      success: true,
      user: data.user,
      session: data.session
    };
  } catch (error: any) {
    console.error('Sign in error:', error);
    return {
      success: false,
      error: error.message || 'Invalid login credentials'
    };
  }
};

export const requestPasswordReset = async (email: string) => {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    if (error) throw error;
    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to send reset email'
    };
  }
};

export const updatePassword = async (newPassword: string) => {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) throw error;
    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to update password'
    };
  }
};

export const getSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    return session;
  } catch (error: any) {
    console.error('Session error:', error);
    return null;
  }
};

// Function to check if user is an admin
export const isUserAdmin = async (): Promise<boolean> => {
  try {
    // Check if user is CEO (hamza@avnearth.com)
    const { data: { user } } = await supabase.auth.getUser();
    if (user?.email === 'hamza@avnearth.com' || user?.email === 'watershedwisdomcouncils@gmail.com') {
      return true;
    }
    
    // For other users, check admin role
    const { data, error } = await supabase.rpc('has_role', {
      p_role_name: 'admin'
    });
    
    if (error) {
      console.error('Admin check error:', error);
      return false;
    }
    
    return data || false;
  } catch (error) {
    console.error('Admin check error:', error);
    return false;
  }
};

// Function to get user roles
export const getUserRoles = async (): Promise<string[]> => {
  try {
    const { data, error } = await supabase.rpc('get_user_roles');
    
    if (error) {
      console.error('Get roles error:', error);
      return [];
    }
    
    return data ? data.map((role: any) => role.role_name) : [];
  } catch (error) {
    console.error('Get roles error:', error);
    return [];
  }
};

export const refreshSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.refreshSession();
    if (error) throw error;
    return session;
  } catch (error: any) {
    console.error('Session refresh error:', error);
    return null;
  }
};

export const signOut = async () => {
  try {
    // Attempt to sign out with retries
    await retryOperation(async () => {
      const { error } = await supabase.auth.signOut();
      if (error) {
        // Check for specific error types
        if (error.message?.includes('Failed to fetch')) {
          throw new Error('Network connection error. Please check your internet connection.');
        }
        if (error.message?.includes('CORS')) {
          throw new Error('Cross-origin request blocked. Please try again.');
        }
        throw error;
      }
    });

    return { success: true };
  } catch (error: any) {
    console.error('Signout error:', {
      message: error.message,
      timestamp: new Date().toISOString(),
      // Log additional context that might be helpful for debugging
      url: supabase.supabaseUrl,
      hasAuthHeader: !!supabase.auth.headers,
    });

    return { 
      success: false, 
      error: error.message || 'Failed to sign out. Please try again.'
    };
  }
};

// Function to sign in as demo user
export const signInAsDemo = async () => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: 'edward@avn.network',
      password: 'AVNedward2024!'
    });

    if (error) throw error;

    return {
      success: true,
      user: data.user,
      session: data.session
    };
  } catch (error: any) {
    console.error('Demo sign in error:', error);
    return {
      success: false,
      error: error.message || 'Failed to access demo account'
    };
  }
};